import React, { useState, useEffect } from "react";
import {
  Paper,
  Tabs,
  Tab,
  Slider,
  Button,
  IconButton,
  Tooltip,
  Popover,
  MenuItem,
  InputBase,
  Grid,
  useTheme,
  Dialog,
  DialogContent,
  Typography,
  Box,
} from "@mui/material";
import { SketchPicker } from "react-color";
import {
  Download as DownloadIcon,
  Info as InfoIcon,
  Search as SearchIcon,
  Gradient as GradientIcon,
  Palette as PaletteIcon,
  Image as ImageIcon,
  Tune as TuneIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ChevronLeft,
  ChevronRight,
  CloudUpload,
} from "@mui/icons-material";
import TabPanel from "./TabPanel";
import { motion } from "framer-motion";

const predefinedColors = [
  "#FFFFFF",
  "#2A2A2A",
  "#1E1E1E",
  "#121212",
  "#FF4C4C",
  "#4CAF50",
  "#2196F3",
  "#FFD700",
  "#9C27B0",
  "#00BCD4",
  "#E91E63",
];

const predefinedGradients = [
  "linear-gradient(45deg, #2c3e50, #3498db)",
  "linear-gradient(135deg, #1a1a1a, #404040)",
  "linear-gradient(45deg, #232526, #414345)",
  "linear-gradient(45deg, #000000, #434343)",
];

const SidebarSection = ({
  activeTab,
  setActiveTab,
  backgroundColor,
  setBackgroundColor,
  setBackgroundImage,
  searchQuery,
  setSearchQuery,
  unsplashImages,
  editSettings,
  setEditSettings,
  handleSearchUnsplash,
  currentPage,
  totalPages,
}) => {
  const theme = useTheme();
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedGradient, setSelectedGradient] = useState("");
  const [customGradient, setCustomGradient] = useState("");
  const [localPreview, setLocalPreview] = useState(null);

  // Remove local currentPage state
  const handleSearch = () => {
    handleSearchUnsplash(1);
  };

  const handlePageChange = (newPage) => {
    handleSearchUnsplash(newPage);
  };

  useEffect(() => {
    if (activeTab === 1 && !searchQuery) {
      setSearchQuery("dark abstract");
      handleSearchUnsplash(1);
    }
  }, [activeTab]);

  const isValidGradient = (value) => {
    return /^(linear|radial|conic)-gradient\(.*\)$/.test(value);
  };

  const handleGradientApply = () => {
    if (isValidGradient(customGradient)) {
      setBackgroundImage(customGradient);
      setBackgroundColor("transparent");
      setSelectedGradient(customGradient);
    }
  };

  const handleLocalImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setBackgroundImage(imageUrl);
      setBackgroundColor("transparent");
      setSelectedGradient("");
      setLocalPreview(imageUrl);
    }
  };
  return (
    <Grid spacing={2} sx={{ height: "100vh", p: 2 }}>
      {/* Main Sidebar Content */}
      <Grid item xs={15} md={15}>
        <Paper
          elevation={2}
          sx={{
            backgroundColor: "#121212",
            color: "#ffffff",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            borderRadius: "16px",
            boxShadow: "0px 8px 24px rgba(0,0,0,0.4)",
            border: "1px solid rgba(255,255,255,0.1)",
            width: "350px!important",
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            variant="fullWidth"
            indicatorColor="secondary"
            textColor="secondary"
            sx={{
              "& .MuiTabs-indicator": {
                height: 3,
                backgroundColor: theme.palette.secondary.main,
              },
              flexShrink: 0,
              background: "linear-gradient(145deg, #1a1a1a, #0d0d0d)",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              borderRadius: "16px 16px 0 0",
            }}
          >
            <Tab
              icon={<PaletteIcon sx={{ fontSize: "1.2rem", color: "gray" }} />}
              label={
                <Typography variant="caption" sx={{ color: "gray" }}>
                  Background
                </Typography>
              }
              sx={{ py: 1.5, textTransform: "capitalize" }}
            />
            <Tab
              icon={<ImageIcon sx={{ fontSize: "1.2rem", color: "gray" }} />}
              label={
                <Typography variant="caption" sx={{ color: "gray" }}>
                  Image
                </Typography>
              }
              sx={{ py: 1.5, textTransform: "capitalize" }}
            />
            <Tab
              icon={<TuneIcon sx={{ fontSize: "1.2rem", color: "gray" }} />}
              label={
                <Typography variant="caption" sx={{ color: "gray" }}>
                  Edit
                </Typography>
              }
              sx={{ py: 1.5, textTransform: "capitalize" }}
            />
          </Tabs>

          {/* Tab Panels */}
          <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
            {/* Background Tab */}
            <TabPanel value={activeTab} index={0}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="overline"
                  sx={{ color: "rgba(255,255,255,0.6)", letterSpacing: 1 }}
                >
                  Color Presets
                </Typography>
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  {predefinedColors.map((color) => (
                    <Grid item xs={3} key={color}>
                      <motion.div whileHover={{ scale: 1.05 }}>
                        <Paper
                          sx={{
                            height: 40,
                            backgroundColor: color,
                            borderRadius: "8px",
                            cursor: "pointer",
                            border:
                              color === backgroundColor
                                ? `2px solid ${theme.palette.secondary.main}`
                                : "none",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                          }}
                          onClick={() => {
                            setBackgroundColor(color);
                            setBackgroundImage(null);
                            setSelectedGradient("");
                          }}
                        />
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{ color: "rgba(255,255,255,0.6)", letterSpacing: 1 }}
                  >
                    Custom Color
                  </Typography>
                  <Tooltip title="Custom Color Picker">
                    <IconButton
                      size="small"
                      onClick={() => setShowColorPicker(true)}
                      sx={{
                        color: "rgba(255,255,255,0.6)",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.08)",
                        },
                      }}
                    >
                      <GradientIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Dialog
                  open={showColorPicker}
                  onClose={() => setShowColorPicker(false)}
                  PaperProps={{
                    sx: {
                      background: "black",
                      boxShadow: "none",
                      opacity: "0.8",
                      overflow: "hidden", // Hides overflow
                    },
                  }}
                  sx={{
                    "& .MuiDialog-scrollPaper": {
                      overflow: "hidden", // Hides scrollbar
                    },
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <SketchPicker
                      color={backgroundColor}
                      onChangeComplete={(color) => {
                        setBackgroundColor(color.hex);
                        setBackgroundImage(null);
                        setSelectedGradient("");
                      }}
                      styles={{
                        picker: {
                          background: "#1E1E1E !important",
                          borderRadius: "12px !important",
                          boxShadow: "0 12px 24px rgba(0,0,0,0.4) !important",
                          border: "1px solid rgba(255,255,255,0.1) !important",
                        },
                      }}
                    />
                    <IconButton
                      onClick={() => setShowColorPicker(false)}
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        color: "#fff",
                        background: "rgba(0,0,0,0.4)",
                        "&:hover": { background: "rgba(0,0,0,0.6)" },
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Dialog>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="overline"
                  sx={{ color: "rgba(255,255,255,0.6)", letterSpacing: 1 }}
                >
                  Gradient Backgrounds
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  {predefinedGradients.map((gradient) => (
                    <Grid item xs={6} key={gradient}>
                      <motion.div whileHover={{ scale: 1.02 }}>
                        <Paper
                          sx={{
                            height: 80,
                            background: gradient,
                            borderRadius: "8px",
                            cursor: "pointer",
                            border:
                              gradient === selectedGradient
                                ? `2px solid ${theme.palette.secondary.main}`
                                : "none",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                          }}
                          onClick={() => {
                            setBackgroundImage(gradient);
                            setBackgroundColor("transparent");
                            setSelectedGradient(gradient);
                          }}
                        />
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              <Box>
                <InputBase
                  fullWidth
                  placeholder="linear-gradient(45deg, #ff0000, #00ff00)"
                  value={customGradient}
                  onChange={(e) => setCustomGradient(e.target.value)}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.08)",
                    borderRadius: "8px",
                    px: 2,
                    py: 1,
                    mb: 1,
                    color: "#fff",
                    fontFamily: "monospace",
                    "& input::placeholder": { color: "rgba(255,255,255,0.4)" },
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleGradientApply}
                  startIcon={<CheckIcon />}
                  sx={{
                    background: "linear-gradient(45deg, #2196F3, #4CAF50)",
                    fontWeight: "bold",
                    letterSpacing: 0.5,
                    "&:hover": { opacity: 0.9 },
                  }}
                >
                  Apply Custom Gradient
                </Button>
              </Box>
            </TabPanel>

            {/* Image Tab */}
            <TabPanel value={activeTab} index={1}>
                  {/* Local Image Upload Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            sx={{ color: "rgba(255,255,255,0.6)", letterSpacing: 1 }}
          >
            Local Image
          </Typography>
          <Button
            component="label"
            variant="outlined"
            fullWidth
            startIcon={<CloudUpload />}
            sx={{
              mt: 1,
              color: "#fff",
              borderColor: "rgba(255,255,255,0.2)",
              '&:hover': {
                borderColor: theme.palette.secondary.main,
              }
            }}
          >
            Upload Image
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleLocalImageUpload}
            />
          </Button>
          {localPreview && (
            <Paper
              sx={{
                mt: 2,
                position: "relative",
                paddingTop: "100%",
                borderRadius: "8px",
                overflow: "hidden",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
              }}
            >
              <img
                src={localPreview}
                alt="Local preview"
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            </Paper>
          )}
        </Box>
              <Box sx={{ mb: 3 }}>
                <InputBase
                  fullWidth
                  placeholder="Search Unsplash..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.08)",
                    borderRadius: "8px",
                    px: 2,
                    py: 1,
                    color: "#fff",
                    "& input::placeholder": { color: "rgba(255,255,255,0.4)" },
                  }}
                  endAdornment={
                    <IconButton
                      onClick={handleSearch}
                      sx={{ color: "rgba(255,255,255,0.6)" }}
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  }
                />
              </Box>

              <Grid
                container
                spacing={2}
                sx={{
                  height: "500px",
                  overflowY: "scroll",
                  "&::-webkit-scrollbar": { display: "none" },
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                }}
              >
                {unsplashImages.map((img) => (
                  <Grid item xs={6} key={img.id}>
                    <motion.div whileHover={{ scale: 1.03 }}>
                      <Paper
                        sx={{
                          position: "relative",
                          paddingTop: "100%",
                          borderRadius: "8px",
                          overflow: "hidden",
                          cursor: "pointer",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                        }}
                        onClick={() => {
                          setBackgroundImage(img.urls.regular);
                          setBackgroundColor("transparent");
                        }}
                      >
                        <img
                          src={img.urls.thumb}
                          alt={img.alt_description}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Paper>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 2,
                  gap: 1,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<ChevronLeft />}
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.2)",
                    "&:disabled": { opacity: 0.5 },
                  }}
                >
                  Previous
                </Button>

                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.6)" }}
                >
                  Page {currentPage} of {totalPages}
                </Typography>

                <Button
                  variant="outlined"
                  endIcon={<ChevronRight />}
                  disabled={currentPage >= totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  sx={{
                    color: "#fff",
                    borderColor: "rgba(255,255,255,0.2)",
                    "&:disabled": { opacity: 0.5 },
                  }}
                >
                  Next
                </Button>
              </Box>
            </TabPanel>

            {/* Edit Tab */}
            <TabPanel value={activeTab} index={2}>
              {Object.entries(editSettings).map(([key, value]) => (
                <Box key={key} sx={{ mb: 3 }}>
                  <Typography
                    variant="overline"
                    sx={{ color: "rgba(255,255,255,0.6)", letterSpacing: 1 }}
                  >
                    {key.charAt(0).toUpperCase() + key.slice(1)}
                  </Typography>
                  <Slider
                    value={value}
                    onChange={(e, v) =>
                      setEditSettings((prev) => ({ ...prev, [key]: v }))
                    }
                    min={0}
                    max={200}
                    sx={{
                      color: theme.palette.secondary.main,
                      "& .MuiSlider-thumb": {
                        width: 14,
                        height: 14,
                        boxShadow: "0 2px 4px rgba(0,0,0,0.4)",
                      },
                      "& .MuiSlider-valueLabel": {
                        backgroundColor: "#1E1E1E",
                        color: "#fff",
                        border: "1px solid rgba(255,255,255,0.1)",
                      },
                    }}
                  />
                </Box>
              ))}

         
            </TabPanel>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default SidebarSection;
